// Lane A — client-side EVM crypto sends. The user's own wallet (MetaMask/Rabby/Coinbase)
// signs & broadcasts; we never touch keys. v1 targets Base Sepolia (testnet) so QA is free
// and safe. Structured so mainnet Base + Ethereum can be flipped on later.

export const NETWORKS = {
  baseSepolia: {
    key: "base-sepolia",
    chainId: 84532,
    hexChainId: "0x14a34",
    name: "Base Sepolia",
    testnet: true,
    rpc: "https://sepolia.base.org",
    explorer: "https://sepolia.basescan.org",
    nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  },
  // KROWN Network — EVM L1 (chainId 1983). Native coin KROWN. Not yet on an exchange/CoinGecko
  // (USD hidden). Used for token-gated BLOQs + wallet-verified identity.
  krown: {
    key: "krown",
    chainId: 1983,
    hexChainId: "0x7bf",
    name: "KROWN Network",
    testnet: false,
    rpc: "https://mainnet.krown.network",
    explorer: "https://explorer.krown.network",
    nativeCurrency: { name: "Krown", symbol: "KROWN", decimals: 18 },
  },
};

// Active network for v1 (testnet). Flip to a mainnet entry at launch.
export const ACTIVE_NETWORK = NETWORKS.baseSepolia;

// Sendable tokens on the active network. USDC = Circle's official Base Sepolia test token.
export const TOKENS = [
  { symbol: "USDC", label: "USD Coin", kind: "erc20", address: "0x036CbD53842c5426634e7929541eC2318f3dCF7e", decimals: 6, coingeckoId: "usd-coin", stable: true },
  { symbol: "ETH", label: "Ether", kind: "native", decimals: 18, coingeckoId: "ethereum", stable: false },
];

export const explorerTxUrl = (hash) => `${ACTIVE_NETWORK.explorer}/tx/${hash}`;
export const explorerAddrUrl = (addr) => `${ACTIVE_NETWORK.explorer}/address/${addr}`;
export const isValidEvmAddress = (addr) => /^0x[a-fA-F0-9]{40}$/.test(String(addr || "").trim());
export const shortAddr = (addr) => (addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : "");

// Decimal string → BigInt base units (no floats — avoids precision loss).
export function parseUnits(value, decimals) {
  const s = String(value).trim();
  if (!/^\d*\.?\d*$/.test(s) || s === "" || s === ".") throw new Error("Enter a valid amount.");
  const [whole = "0", frac = ""] = s.split(".");
  if (frac.length > decimals) throw new Error(`${decimals} decimals max.`);
  const padded = (frac + "0".repeat(decimals)).slice(0, decimals);
  return BigInt(whole || "0") * 10n ** BigInt(decimals) + BigInt(padded || "0");
}

// ERC-20 transfer(address,uint256) calldata — hand-encoded, no library needed.
function erc20TransferData(to, amountBase) {
  const selector = "a9059cbb";
  const addr = to.toLowerCase().replace(/^0x/, "").padStart(64, "0");
  const amt = amountBase.toString(16).padStart(64, "0");
  return `0x${selector}${addr}${amt}`;
}

async function ensureChain(eth, net) {
  const current = await eth.request({ method: "eth_chainId" });
  if (String(current).toLowerCase() === net.hexChainId) return;
  try {
    await eth.request({ method: "wallet_switchEthereumChain", params: [{ chainId: net.hexChainId }] });
  } catch (e) {
    if (e?.code === 4902 || /unrecognized|not been added|add/i.test(e?.message || "")) {
      await eth.request({
        method: "wallet_addEthereumChain",
        params: [{
          chainId: net.hexChainId,
          chainName: net.name,
          nativeCurrency: net.nativeCurrency,
          rpcUrls: [net.rpc],
          blockExplorerUrls: [net.explorer],
        }],
      });
    } else {
      throw e;
    }
  }
}

// Prompt the wallet to switch to (or add) KROWN Network (EIP-3085). Best-effort — call before a KROWN
// action so generic wallets get the network added; Qastle (KROWN-native) just no-ops. Pass a WalletConnect
// EIP-1193 provider, or omit to use window.ethereum.
export async function ensureKrownNetwork(provider) {
  const eth = provider || window.ethereum;
  if (!eth?.request) return;
  await ensureChain(eth, NETWORKS.krown);
}

// Connect the injected EVM wallet, ensure the right chain, and broadcast the transfer.
// Pass `provider` to broadcast through a WalletConnect (Qastle) EIP-1193 provider instead of window.ethereum.
// Returns { hash, from }. Throws friendly errors (use isUserRejection to detect cancels).
export async function sendCrypto({ token, to, amount, provider }) {
  const net = ACTIVE_NETWORK;
  const eth = provider || window.ethereum;
  if (!eth) throw new Error("No EVM wallet detected. Install MetaMask, Rabby or Coinbase Wallet.");
  if (!isValidEvmAddress(to)) throw new Error("Invalid recipient address.");
  const accounts = await eth.request({ method: "eth_requestAccounts" });
  const from = accounts?.[0];
  if (!from) throw new Error("No wallet account selected.");
  await ensureChain(eth, net);

  const base = parseUnits(amount, token.decimals);
  if (base <= 0n) throw new Error("Amount must be greater than 0.");

  const txParams =
    token.kind === "native"
      ? { from, to, value: `0x${base.toString(16)}` }
      : { from, to: token.address, value: "0x0", data: erc20TransferData(to, base) };

  const hash = await eth.request({ method: "eth_sendTransaction", params: [txParams] });
  return { hash, from };
}

// Live USD price (CoinGecko free public API, CORS-enabled). Display-only; caller caches.
export async function fetchUsdPrice(coingeckoId) {
  if (!coingeckoId) return null;
  try {
    const r = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${coingeckoId}&vs_currencies=usd`);
    if (!r.ok) return null;
    const d = await r.json();
    return d?.[coingeckoId]?.usd ?? null;
  } catch {
    return null;
  }
}
